/**
 * Ürün Listesi — eski `products.php` karşılığı.
 *
 * Eski ekranın düzeltilen hataları:
 *  · `limit => -1` ile TÜM ürünler tek seferde çekiliyor, her satır için ayrı
 *    dosya sorgusu yapılıyordu (N+1). Burada tek koleksiyon aboneliği + istemci
 *    tarafı filtre/sayfalama var.
 *  · Rol kontrolü hiç yoktu; `production` ürün ekleyip düzenleyebiliyordu (G-11).
 *    Artık üretim SALT OKUR: "Ürün Ekle" butonu yalnızca boss/office'te görünür.
 *  · Listede MALİYET/KÂR sütunu YOKTUR — hiçbir rol için. Maliyet
 *    `products/{id}/private/cost` alt dokümanındadır ve liste ekranında toplu
 *    okunması hem Rules'a hem gizlilik sınırına aykırıdır.
 *
 * Bu ekran grubunun beklediği rotalar (router başka ajanda):
 *   /products                → Products
 *   /products/new           → ProductDetail (yeni kayıt modu)
 *   /products/:productId            → ProductDetail
 *   /products/:productId/recipe     → Recipe
 *   /products/:productId/packaging      → Packaging
 *   /parcalar               → Components
 *   /satin-alma             → Procurement
 *   /kullanicilar           → Users
 *   /ayarlar                → Settings
 *   /raporlar               → Reports
 *   /denetim                → AuditLog
 */

import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { where, orderBy } from "firebase/firestore";
import { PageHeader, Spinner, EmptyState, StatusBadge, useToast } from "../components";
import { useCollection, useCallableAction } from "../hooks";
import { useCan } from "../lib/auth";
import { tl, n } from "../lib/format";
import { ErrorNote, Field } from "../components";

/**
 * ★ ÜRÜN STATÜSÜ SHOPIFY SÖZLÜĞÜNDEDİR.
 *
 * Sunucu (`shopify/mappers.ts:normalizeProductStatus`) "ACTIVE"/"DRAFT"/
 * "ARCHIVED" yazıyor ve `firestore.rules` yalnızca bu üçünü kabul ediyor.
 * Arayüz ise WooCommerce'ten kalma "publish"/"draft"/"private"/"archived"
 * kullanıyordu; sonuç: kural 516 ürünün 516'sını REDDEDİYOR, hiçbir ürün
 * kaydedilemiyordu. Otorite Shopify olduğu için sözlük ona hizalandı.
 */
export type ProductStatus = "ACTIVE" | "DRAFT" | "ARCHIVED";

export interface ProductDoc {
  /**
   * TEDARİK POLİTİKASI — "hızlı gönderim" ayrımının tutulduğu tek yer.
   * `madeToStock` ürünler stoğa üretilir ve stoktan gönderilir (1 iş günü);
   * diğerleri sipariş üzerine üretilir (10 iş günü). Bu bilgi eski sistemde
   * hiçbir alanda yoktu, ekip stok siparişi geçmişinden hatırlıyordu.
   */
  stockPolicy?: "madeToOrder" | "madeToStock";
  /** İş günü. `null` ise `settings/delivery` varsayılanı geçerlidir. */
  leadTimeDays?: number | null;
  /**
   * `shopify` = `ayni-gun-kargo` etiketinden (gece taraması, geçerli kaynak);
   * `history` / `manual` = eşitlemeden önceki eski değerler.
   */
  stockPolicySource?: "history" | "manual" | "shopify";
  /**
   * Durum ve hızlı gönderim Shopify'dan eşitlendi (`shopify/priceSync.ts`).
   * Doluysa ürün kartındaki durum seçimi KİLİTLİDİR — Shopify'da değiştirilir.
   */
  statusSyncedAt?: unknown;
  isActive?: boolean;
  stockPolicyEvidence?: { stockOrderCount?: number; totalQuantity?: number } | null;

  id: string;
  name: string;
  sku?: string | null;
  /** KURUŞ (integer). Vitrin fiyatı, KDV dâhil. */
  price?: number | null;
  status?: ProductStatus;
  barcode?: string | null;
  /** kg */
  weightNet?: number | null;
  /** kg */
  weightPacked?: number | null;
  imageUrl?: string | null;
  productionNote?: string | null;
  /** Parite eşiği bu kategoriye göre seçilir (Oturma Grubu 3,5 · Karyola 3). */
  categoryName?: string | null;
  productType?: string | null;
  hasRecipe?: boolean;
  recipeLineCount?: number;
  packagingLineCount?: number;
  externalId?: string | null;
  source?: string | null;
  deleted?: boolean;
}

/** Eski `configure.php:28` $StatusTurkish haritasının yeni karşılığı. */
export const PRODUCT_STATUS_TR: Record<ProductStatus, string> = {
  ACTIVE: "Yayında",
  DRAFT: "Taslak",
  ARCHIVED: "Arşiv",
};

export function productStatusTone(s: ProductStatus | undefined) {
  if (s === "ACTIVE") return "success" as const;
  if (s === "ARCHIVED") return "danger" as const;
  return "neutral" as const;
}

const INPUT =
  "w-full rounded border border-slate-300 px-2 py-1.5 text-sm focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500";

const PAGE_SIZE = 50;

/**
 * SATIŞ FİYATLARINI SHOPIFY'DAN TAZELE.
 *
 * Fiyat otoritesi Shopify'dır; Balder aynadır. Ayna eskirse yalnızca liste
 * yanlış görünmez — maliyet motoru satış fiyatını buradan okuduğu için kâr
 * oranı, parite ve "fiyat ucuz" uyarısı da eski fiyattan hesaplanır.
 *
 * Sonuç kaç ürünün DEĞİŞTİĞİNİ söyler; "senkronlandı" deyip geçmek, hiçbir şey
 * değişmediyse de değiştiyse de aynı cümleyi kurmak olurdu.
 */
function PriceSyncButton() {
  const toast = useToast();
  const run = useCallableAction<
    { dryRun?: boolean },
    {
      scanned: number; matched: number; updated: number; variantsUpdated: number;
      missingInBalder: number; noPrice: number;
      /** Yayında/Taslak/Arşiv durumu Shopify'a göre değişen ürün sayısı. */
      statusUpdated?: number;
      /** Hızlı gönderim (`ayni-gun-kargo` etiketi) değişen ürün sayısı. */
      policyUpdated?: number;
    }
  >("syncProductPrices");

  const onRun = async () => {
    const out = await run.run({});
    if (!out) {
      toast.error(run.error ?? "Ürünler güncellenemedi.");
      return;
    }
    const durum = out.statusUpdated ?? 0;
    const politika = out.policyUpdated ?? 0;
    if (out.updated === 0 && out.variantsUpdated === 0 && durum === 0 && politika === 0) {
      toast.info(`Fiyat, durum ve hızlı gönderim zaten güncel — ${out.scanned} ürün karşılaştırıldı.`);
      return;
    }
    const baslik = [
      out.updated ? `${out.updated} ürünün fiyatı` : "",
      durum ? `${durum} ürünün durumu (Yayında/Taslak)` : "",
      politika ? `${politika} ürünün hızlı gönderim bilgisi` : "",
    ].filter(Boolean).join(", ");
    toast.success(
      `${baslik} Shopify'a göre güncellendi.`,
      `${out.scanned} ürün tarandı, ${out.variantsUpdated} varyant fiyatı tazelendi` +
        (out.missingInBalder ? `, ${out.missingInBalder} ürün Balder'de yok` : "") +
        (out.noPrice ? `, ${out.noPrice} üründe fiyat okunamadı` : "") +
        (out.updated ? ". Maliyet ve kâr oranları yeniden hesaplanıyor." : "."),
    );
  };

  return (
    <button
      type="button"
      onClick={() => void onRun()}
      disabled={run.pending}
      title="Shopify'daki satış fiyatını, Yayında/Taslak durumunu ve hızlı gönderim etiketini Balder'e işler. Her gece 03:30'da kendiliğinden de çalışır."
      className="rounded border border-slate-300 px-3 py-2 text-sm hover:bg-slate-100 disabled:opacity-50"
    >
      {run.pending ? "Shopify'dan alınıyor…" : "Shopify'dan güncelle"}
    </button>
  );
}

export default function Products() {
  const can = useCan();
  const { data, loading, error } = useCollection<ProductDoc>("products", [
    where("deleted", "==", false),
    orderBy("name"),
  ]);

  const [q, setQ] = useState("");
  const [status, setStatus] = useState<"" | ProductStatus>("");
  const [onlyMissingRecipe, setOnlyMissingRecipe] = useState(false);
  const [onlyFastShip, setOnlyFastShip] = useState(false);
  const [page, setPage] = useState(1);

  /**
   * ★ KUMAŞ VARYASYONLARI LİSTEDE GÖSTERİLMEZ — yalnızca ana stok kodu.
   *
   * Kumaşlı ürünün bir kaydı ana SKU ile durur ("Basic Sandalye", 6014), renkler
   * ayrı kayıt DEĞİLDİR. Rida Sandalye ise göçten 22 renk kaydıyla geldi
   * (`6549.VB`, `6549.KL`…, Shopify varyant kimlikleriyle); liste her rengi ayrı
   * ürün gibi gösteriyordu. Noktalı SKU'lu kayıt, TABANINI taşıyan bir ana kayıt
   * varsa gizlenir. Kayıtlar SİLİNMEZ: siparişler ve fiyat eşitlemesi onlara
   * bağlı. Ana kaydı olmayan noktalı SKU görünmeye devam eder — gizlemek onu
   * kaybettirirdi.
   */
  const { visible, hiddenVariantCount } = useMemo(() => {
    const anaSkular = new Set(
      data
        .map((p) => (p.sku ?? "").trim().toLocaleUpperCase("tr"))
        .filter((s) => s !== "" && !s.includes(".")),
    );
    const gorunen = data.filter((p) => {
      const s = (p.sku ?? "").trim().toLocaleUpperCase("tr");
      const nokta = s.indexOf(".");
      return !(nokta > 0 && anaSkular.has(s.slice(0, nokta)));
    });
    return { visible: gorunen, hiddenVariantCount: data.length - gorunen.length };
  }, [data]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLocaleLowerCase("tr");
    return visible.filter((p) => {
      if (status && p.status !== status) return false;
      if (onlyMissingRecipe && p.hasRecipe !== false) return false;
      if (onlyFastShip && p.stockPolicy !== "madeToStock") return false;
      if (!needle) return true;
      return (
        p.name.toLocaleLowerCase("tr").includes(needle) ||
        (p.sku ?? "").toLocaleLowerCase("tr").includes(needle) ||
        (p.barcode ?? "").toLocaleLowerCase("tr").includes(needle)
      );
    });
  }, [visible, q, status, onlyMissingRecipe, onlyFastShip]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const rows = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const missingRecipeCount = useMemo(
    // Gizli kumaş varyasyonlarının reçetesi ana kayıttadır; uyarıyı şişirmesin.
    () => visible.filter((p) => p.hasRecipe === false).length,
    [visible],
  );

  return (
    <PageHeader title="Ürünler">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <p className="max-w-2xl text-sm text-slate-500">
          Satış kataloğu. Maliyet ve kâr bilgisi bu listede <strong>hiçbir rol için</strong>
          gösterilmez; ürün kartındaki ayrı blokta ve yalnızca yetkili kullanıcıya açılır.
        </p>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => window.print()}
            className="rounded border border-slate-300 px-3 py-2 text-sm hover:bg-slate-100"
          >
            Yazdır
          </button>
          {/* ★ Satış fiyatları Shopify'da değişiyor, Balder'de eskiyordu:
              `products/update` webhook'u fiyata hiç dokunmuyordu. Artık
              dokunuyor ve gece 03:30'da tarama da var — ama fiyat değişikliği
              hemen görünsün isteyen için elle tetikleme düğmesi. */}
          {can.isStaff ? <PriceSyncButton /> : null}
          {can.isStaff ? (
            <Link
              to="/products/new"
              className="rounded-lg bg-gray-900 px-3 py-2 text-sm font-medium text-white hover:bg-gray-800"
            >
              Ürün Ekle
            </Link>
          ) : null}
        </div>
      </div>

      {/* Eski sistemde reçetesiz ürün sessizce üretime düşüyor, stok hiç düşmüyordu. */}
      {missingRecipeCount > 0 ? (
        <div className="mb-3 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          {n(missingRecipeCount)} üründe reçete tanımlı değil. Reçetesiz ürünlerde stok düşümü ve
          maliyet hesabı çalışmaz.
          <button
            type="button"
            className="ml-2 underline"
            onClick={() => {
              setOnlyMissingRecipe(true);
              setPage(1);
            }}
          >
            Listele
          </button>
        </div>
      ) : null}

      <div className="mb-3 grid grid-cols-1 gap-2 rounded border border-slate-200 bg-slate-50 p-3 md:grid-cols-4">
        <div className="md:col-span-2">
          <Field label="Ara (ad / SKU / barkod)">
            <input
              className={INPUT}
              value={q}
              placeholder="Ürün adı, SKU veya barkod"
              onChange={(e) => {
                setQ(e.target.value);
                setPage(1);
              }}
            />
          </Field>
        </div>
        <Field label="Statü">
          <select
            className={INPUT}
            value={status}
            onChange={(e) => {
              setStatus(e.target.value as "" | ProductStatus);
              setPage(1);
            }}
          >
            <option value="">Tüm Statüler</option>
            {(Object.keys(PRODUCT_STATUS_TR) as ProductStatus[]).map((s) => (
              <option key={s} value={s}>
                {PRODUCT_STATUS_TR[s]}
              </option>
            ))}
          </select>
        </Field>
        <label className="flex items-end gap-2 pb-1.5 text-sm text-slate-700">
          <input
            type="checkbox"
            checked={onlyMissingRecipe}
            onChange={(e) => {
              setOnlyMissingRecipe(e.target.checked);
              setPage(1);
            }}
          />
          Yalnızca reçetesiz ürünler
        </label>
        <label className="flex items-end gap-2 pb-1.5 text-sm text-slate-700">
          <input
            type="checkbox"
            checked={onlyFastShip}
            onChange={(e) => {
              setOnlyFastShip(e.target.checked);
              setPage(1);
            }}
          />
          Yalnızca hızlı gönderim
        </label>
      </div>

      {error ? <ErrorNote error={error} /> : null}
      {loading ? <Spinner /> : null}

      {!loading && filtered.length === 0 ? (
        <EmptyState title="Ürün bulunamadı" description="Arama ve filtreleri gözden geçirin." />
      ) : null}

      {!loading && filtered.length > 0 ? (
        <>
          <div className="mb-2 flex items-center justify-between text-xs text-slate-500">
            <span>
              Toplam: {n(filtered.length)} ürün
              {hiddenVariantCount > 0
                ? ` · ${n(hiddenVariantCount)} kumaş varyasyonu ana ürününün altında gizli`
                : ""}
            </span>
            <span>
              Sayfa {safePage} / {totalPages}
            </span>
          </div>

          <div className="overflow-x-auto rounded border border-slate-200">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-100 text-left text-xs uppercase tracking-wide text-slate-600">
                <tr>
                  {/* ★ TABLET/TELEFON: 8 sütun telefonda 657px yer istiyordu
                      (343px kap). İkincil sütunlar dar ekranda gizlenir; SKU
                      "Ürün" hücresinin altına iner. Masaüstü (lg+) değişmez. */}
                  <th className="px-3 py-2">Ürün</th>
                  <th className="hidden px-3 py-2 md:table-cell">SKU</th>
                  <th className="px-3 py-2 text-right">Satış Fiyatı</th>
                  <th className="hidden px-3 py-2 lg:table-cell">Reçete</th>
                  <th className="hidden px-3 py-2 lg:table-cell">Gönderim</th>
                  <th className="hidden px-3 py-2 sm:table-cell">Statü</th>
                  <th className="hidden px-3 py-2 text-right lg:table-cell">Ağırlık (kg)</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((p) => (
                  <tr key={p.id} className="hover:bg-slate-50">
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        {p.imageUrl ? (
                          <img
                            src={p.imageUrl}
                            alt=""
                            className="h-8 w-8 shrink-0 rounded object-cover"
                            loading="lazy"
                          />
                        ) : (
                          <span className="h-8 w-8 shrink-0 rounded bg-slate-200" />
                        )}
                        <Link
                          to={`/products/${p.id}`}
                          className="font-medium text-sky-700 hover:underline"
                        >
                          {p.name}
                        </Link>
                      </div>
                      {/* Dar ekranda gizlenen SKU ve statü burada görünür. */}
                      <span className="mt-1 block text-xs text-slate-600 md:hidden">
                        {p.sku || "—"}
                      </span>
                      <span className="mt-1 block sm:hidden">
                        <StatusBadge
                          label={PRODUCT_STATUS_TR[p.status ?? "DRAFT"]}
                          color={productStatusTone(p.status)}
                        />
                      </span>
                    </td>
                    <td className="hidden px-3 py-2 text-slate-600 md:table-cell">
                      {p.sku || "—"}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">{tl(p.price)}</td>
                    <td className="hidden px-3 py-2 lg:table-cell">
                      {p.hasRecipe === false ? (
                        <StatusBadge label="Yok" color="warning" />
                      ) : (
                        <span className="text-slate-600">{n(p.recipeLineCount ?? 0)} satır</span>
                      )}
                    </td>
                    <td className="hidden px-3 py-2 lg:table-cell">
                      {/* "Hızlı gönderim" = stoğa üretilen ürün. Ayrım eski
                          sistemde hiç veri değildi; artık `stockPolicy`'de. */}
                      {p.stockPolicy === "madeToStock" ? (
                        <StatusBadge label="Hızlı gönderim" color="green" />
                      ) : (
                        <span className="text-xs text-slate-600">Sipariş üzerine</span>
                      )}
                    </td>
                    <td className="hidden px-3 py-2 sm:table-cell">
                      <StatusBadge
                        label={PRODUCT_STATUS_TR[p.status ?? "DRAFT"]}
                        color={productStatusTone(p.status)}
                      />
                    </td>
                    <td className="hidden px-3 py-2 text-right tabular-nums text-slate-600 lg:table-cell">
                      {/* Net ağırlık asıl kayıttır; paketli ağırlık yalnızca
                          bazı üründe var. İkisi de yoksa tire. */}
                      {p.weightNet ?? p.weightPacked
                        ? n(p.weightNet ?? p.weightPacked)
                        : "—"}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <Link
                        to={`/products/${p.id}`}
                        className="inline-flex min-h-[44px] items-center justify-center rounded border border-slate-300 px-3 py-1 text-xs hover:bg-slate-100 lg:min-h-0 lg:px-2"
                      >
                        Kart
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-3 flex items-center justify-between">
            <button
              type="button"
              disabled={safePage <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="rounded border border-slate-300 px-3 py-1.5 text-sm disabled:opacity-40"
            >
              ← Önceki
            </button>
            <button
              type="button"
              disabled={safePage >= totalPages}
              onClick={() => setPage((p) => p + 1)}
              className="rounded border border-slate-300 px-3 py-1.5 text-sm disabled:opacity-40"
            >
              Sonraki →
            </button>
          </div>
        </>
      ) : null}
    </PageHeader>
  );
}
